import {
  ParseRuntimePortError,
  makeParseCheckpointStoreService,
  makeParseEventSinkService,
  makeParseProviderService,
  makeParserCatalogService,
  makeParseSourceService,
  parseRuntimeLayer,
  runParseProgramEffect,
  type CanonicalBlock,
  type ParseRunEvent,
} from '@okrapdf/parser-sdk';
import { Context, Data, Effect, Layer } from 'effect';
import { readFileSync } from 'fs';

import { originalPdfPath, type LocalParseResult } from './local-capabilities.js';

export type NodeLocalParse = (
  dataDir: string | undefined,
  documentId: string | undefined,
  runId: string,
  idPrefix: string,
) => LocalParseResult;

const NodeLocalParseRuntimeTypeId = 'okrapdf/cli/NodeLocalParseRuntime/TypeId' as const;

export interface NodeLocalParseRequest {
  readonly dataDir: string;
  readonly documentId: string;
  readonly runId: string;
  readonly idPrefix: string;
}

export type NodeLocalParseRuntimeOperation = 'load_source' | 'parse_document';
export type NodeLocalParseRuntimeFailureCode = 'source_read_failed' | 'parse_document_failed';

/** Safe native failure: source paths, document bytes, and arbitrary parser errors stay redacted. */
export class NodeLocalParseRuntimeError extends Data.TaggedError('NodeLocalParseRuntimeError')<{
  readonly operation: NodeLocalParseRuntimeOperation;
  readonly code: NodeLocalParseRuntimeFailureCode;
}> {}

const nodeLocalParseRuntimeErrorCauses = new WeakMap<NodeLocalParseRuntimeError, unknown>();

function nodeLocalParseRuntimeFailure(
  operation: NodeLocalParseRuntimeOperation,
  code: NodeLocalParseRuntimeFailureCode,
  cause: unknown,
): NodeLocalParseRuntimeError {
  const error = new NodeLocalParseRuntimeError({ operation, code });
  nodeLocalParseRuntimeErrorCauses.set(error, cause);
  return error;
}

/** Original adapter failure without retaining it on the safe tagged error. */
export function nodeLocalParseRuntimeErrorCause(error: NodeLocalParseRuntimeError): unknown {
  return nodeLocalParseRuntimeErrorCauses.get(error) ?? error;
}

export function restoreNodeLocalParseRuntimeError<A>(
  program: Effect.Effect<A, NodeLocalParseRuntimeError>,
): Effect.Effect<A, unknown> {
  return program.pipe(Effect.catch((error) => Effect.fail(nodeLocalParseRuntimeErrorCause(error))));
}

export interface NodeLocalParseRuntime {
  readonly [NodeLocalParseRuntimeTypeId]: typeof NodeLocalParseRuntimeTypeId;
  readonly loadSource: (
    request: NodeLocalParseRequest,
  ) => Effect.Effect<Uint8Array, NodeLocalParseRuntimeError>;
  readonly parseDocument: (
    request: NodeLocalParseRequest,
  ) => Effect.Effect<LocalParseResult, NodeLocalParseRuntimeError>;
}

export const NodeLocalParseRuntime: Context.Service<NodeLocalParseRuntime, NodeLocalParseRuntime> =
  Context.Service('okrapdf/cli/NodeLocalParseRuntime');

export interface NodeLocalParseRuntimeMakeOptions {
  readonly loadSource: NodeLocalParseRuntime['loadSource'];
  readonly parseDocument: NodeLocalParseRuntime['parseDocument'];
}

/** Build the Effect service from the two runtime-specific primitives. */
export function make(options: NodeLocalParseRuntimeMakeOptions): NodeLocalParseRuntime {
  return NodeLocalParseRuntime.of({
    [NodeLocalParseRuntimeTypeId]: NodeLocalParseRuntimeTypeId,
    loadSource: Effect.fn('okrapdf/cli.NodeLocalParseRuntime.loadSource')(options.loadSource),
    parseDocument: Effect.fn('okrapdf/cli.NodeLocalParseRuntime.parseDocument')(
      options.parseDocument,
    ),
  });
}

export interface PromiseNodeLocalParseRuntime {
  readonly loadSource: (request: NodeLocalParseRequest) => Promise<Uint8Array>;
  readonly parseDocument: (request: NodeLocalParseRequest) => Promise<LocalParseResult>;
}

/** Promise compatibility view that restores the adapter's exact original failure. */
export function toPromiseNodeLocalParseRuntime(
  runtime: NodeLocalParseRuntime,
): PromiseNodeLocalParseRuntime {
  return {
    loadSource: (request) =>
      Effect.runPromise(restoreNodeLocalParseRuntimeError(runtime.loadSource(request))),
    parseDocument: (request) =>
      Effect.runPromise(restoreNodeLocalParseRuntimeError(runtime.parseDocument(request))),
  };
}

/** Existing Node filesystem + synchronous local-parser adapter. */
export function layerNodeLocalParse(
  parseDocument: NodeLocalParse,
): Layer.Layer<NodeLocalParseRuntime> {
  return Layer.succeed(
    NodeLocalParseRuntime,
    make({
      loadSource: (request) =>
        Effect.try({
          try: () =>
            new Uint8Array(readFileSync(originalPdfPath(request.dataDir, request.documentId))),
          catch: (cause) =>
            nodeLocalParseRuntimeFailure('load_source', 'source_read_failed', cause),
        }),
      parseDocument: (request) =>
        Effect.try({
          try: () =>
            parseDocument(request.dataDir, request.documentId, request.runId, request.idPrefix),
          catch: (cause) =>
            nodeLocalParseRuntimeFailure('parse_document', 'parse_document_failed', cause),
        }),
    }),
  );
}

/** Process-local deterministic target pack for tests and examples. */
export const layerMemory: Layer.Layer<NodeLocalParseRuntime> = Layer.succeed(
  NodeLocalParseRuntime,
  make({
    loadSource: () => Effect.succeed(new Uint8Array([0x25, 0x50, 0x44, 0x46])),
    parseDocument: (request) =>
      Effect.succeed({
        ok: true,
        pageCount: 1,
        ocrUsed: false,
        blocks: [
          {
            id: `${request.idPrefix}_p1`,
            kind: 'paragraph',
            page_number: 1,
            text: 'memory parse',
            created_by_run_id: request.runId,
          },
        ],
      }),
  }),
);

function publicReason(error: unknown): string {
  if (error instanceof ParseRuntimePortError) return error.code;
  return error instanceof Error ? error.message : 'Local parser unavailable.';
}

function canonicalPageBlocks(result: LocalParseResult, page: number): CanonicalBlock[] {
  return result.blocks.flatMap((block) => {
    if (block.page_number !== page) return [];
    const bbox = block.bbox;
    const normalizedBbox =
      typeof bbox === 'object' && bbox !== null && !Array.isArray(bbox)
        ? {
            x: Number((bbox as Record<string, unknown>).x),
            y: Number((bbox as Record<string, unknown>).y),
            w: Number((bbox as Record<string, unknown>).w),
            h: Number((bbox as Record<string, unknown>).h),
          }
        : undefined;
    return [
      {
        type: typeof block.kind === 'string' ? block.kind : 'text',
        ...(typeof block.kind === 'string' ? { label: block.kind } : {}),
        ...(typeof block.text === 'string' ? { value: block.text } : {}),
        ...(normalizedBbox ? { bbox: normalizedBbox } : {}),
        ...(typeof block.confidence === 'number' ? { confidence: block.confidence } : {}),
      },
    ];
  });
}

/**
 * Runtime-neutral Node/self-host program. The local parser remains a
 * whole-document implementation, so this evaluates it once and projects the
 * cached result through the shared per-page provider contract.
 */
export const runNodeLocalParseWithRuntimeEffect = Effect.fn(
  'okrapdf/cli.runNodeLocalParseWithRuntime',
)(function* (options: NodeLocalParseRequest) {
  const nodeRuntime = yield* NodeLocalParseRuntime;
  const sourceId = `${options.documentId}/original.pdf`;
  let cached: LocalParseResult | undefined;
  const readParsed = Effect.fn('okrapdf/cli.readLocalParseResult')(function* () {
    if (cached) return cached;
    const result = yield* nodeRuntime.parseDocument(options).pipe(
      Effect.mapError(
        (error) =>
          new ParseRuntimePortError({
            port: 'provider',
            operation: 'parse_document',
            code: publicReason(nodeLocalParseRuntimeErrorCause(error)),
            retryable: false,
            parserId: 'text-layer',
          }),
      ),
    );
    cached = result;
    if (!result.ok) {
      return yield* new ParseRuntimePortError({
        port: 'provider',
        operation: 'parse_document',
        code: result.reason ?? 'Local parser unavailable.',
        retryable: false,
        parserId: 'text-layer',
      });
    }
    return result;
  });
  const completedPages = new Set<number>();
  const failedPages = new Set<number>();
  const events: ParseRunEvent[] = [];
  const layer = parseRuntimeLayer({
    catalog: makeParserCatalogService((parserId) =>
      parserId === 'text-layer'
        ? Effect.succeed({ parserId, providerId: 'local', kind: 'local' })
        : Effect.fail(
            new ParseRuntimePortError({
              port: 'catalog',
              operation: 'resolve',
              code: 'parser_not_found',
              retryable: false,
              parserId,
            }),
          ),
    ),
    source: makeParseSourceService((requestedSourceId) =>
      requestedSourceId === sourceId
        ? nodeRuntime.loadSource(options).pipe(
            Effect.mapError(
              () =>
                new ParseRuntimePortError({
                  port: 'source',
                  operation: 'load',
                  code: 'source PDF not found on disk',
                  retryable: false,
                }),
            ),
          )
        : Effect.fail(
            new ParseRuntimePortError({
              port: 'source',
              operation: 'load',
              code: 'source_not_found',
              retryable: false,
            }),
          ),
    ),
    provider: makeParseProviderService({
      countPages: () =>
        readParsed().pipe(
          Effect.flatMap((result) =>
            result.pageCount && result.pageCount > 0
              ? Effect.succeed(result.pageCount)
              : Effect.fail(
                  new ParseRuntimePortError({
                    port: 'provider',
                    operation: 'count_pages',
                    code: 'document has no pages',
                    retryable: false,
                    parserId: 'text-layer',
                  }),
                ),
          ),
        ),
      parsePage: (_descriptor, _source, page) =>
        readParsed().pipe(Effect.map((result) => canonicalPageBlocks(result, page))),
    }),
    checkpoint: makeParseCheckpointStoreService({
      load: () =>
        Effect.succeed({
          completedPages: [...completedPages],
          failedPages: [...failedPages],
        }),
      isActive: () => Effect.succeed(true),
      recordPage: (_runId, record) =>
        Effect.sync(() => {
          if ('blocks' in record) completedPages.add(record.page);
          else failedPages.add(record.page);
        }),
      finish: () => Effect.void,
    }),
    events: makeParseEventSinkService((event) =>
      Effect.sync(() => {
        events.push(event);
      }),
    ),
  });
  const result = yield* runParseProgramEffect({
    runId: options.runId,
    sourceId,
    parserId: 'text-layer',
    maxAttempts: 3,
    concurrency: 1,
  }).pipe(Effect.provide(layer));
  if (result.status !== 'completed') {
    const failure = events.find((event) => event.type === 'page_failed');
    return {
      ok: false,
      reason: failure?.type === 'page_failed' ? failure.code : 'Local parser unavailable.',
      blocks: [],
    } satisfies LocalParseResult;
  }
  return cached ?? { ok: false, reason: 'Local parser unavailable.', blocks: [] };
});

function byteArraysEqual(left: Uint8Array, right: Uint8Array): boolean {
  return (
    left.byteLength === right.byteLength && left.every((value, index) => value === right[index])
  );
}

function parseResultsEqual(left: LocalParseResult, right: LocalParseResult): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/** Shared, framework-free contract for every Node local-parse adapter Layer. */
export async function nodeLocalParseRuntimeContractSuite<E>(
  name: string,
  layer: Layer.Layer<NodeLocalParseRuntime, E>,
  request: NodeLocalParseRequest,
  expected: { readonly pageCount: number },
): Promise<LocalParseResult> {
  const native = await Effect.runPromise(
    Effect.gen(function* () {
      const runtime = yield* NodeLocalParseRuntime;
      const source = yield* runtime.loadSource(request);
      const parsed = yield* runtime.parseDocument(request);
      const throughProgram = yield* runNodeLocalParseWithRuntimeEffect(request);
      return { runtime, source, parsed, throughProgram };
    }).pipe(Effect.provide(layer)),
  );

  if (native.source.byteLength === 0) {
    throw new Error(`${name}: source bytes must not be empty`);
  }
  if (!native.parsed.ok || native.parsed.pageCount !== expected.pageCount) {
    throw new Error(`${name}: parser must complete ${expected.pageCount} page(s)`);
  }
  if (!parseResultsEqual(native.throughProgram, native.parsed)) {
    throw new Error(`${name}: the generic parse lifecycle must preserve the adapter result`);
  }

  const compatibility = toPromiseNodeLocalParseRuntime(native.runtime);
  const promiseSource = await compatibility.loadSource(request);
  const promiseParsed = await compatibility.parseDocument(request);
  if (!byteArraysEqual(promiseSource, native.source)) {
    throw new Error(`${name}: native and Promise source reads must match`);
  }
  if (!parseResultsEqual(promiseParsed, native.parsed)) {
    throw new Error(`${name}: native and Promise parse results must match`);
  }

  return native.throughProgram;
}

/** Existing native API, now derived by providing the Node adapter layer. */
export const runNodeLocalParseProgramEffect = Effect.fn('okrapdf/cli.runNodeLocalParseProgram')(
  (options: NodeLocalParseRequest & { readonly parseDocument: NodeLocalParse }) =>
    runNodeLocalParseWithRuntimeEffect(options).pipe(
      Effect.provide(layerNodeLocalParse(options.parseDocument)),
    ),
);

export async function runNodeLocalParseProgram(
  options: Parameters<typeof runNodeLocalParseProgramEffect>[0],
): Promise<LocalParseResult> {
  return Effect.runPromise(
    runNodeLocalParseProgramEffect(options).pipe(
      Effect.catch((error) =>
        Effect.succeed({ ok: false, reason: publicReason(error), blocks: [] }),
      ),
    ),
  );
}
